<template>
  <div
    class="template-dependency-section"
    data-testid="setup-template-dependencies"
  >
    <button
      type="button"
      class="template-dependency-section__header"
      :aria-expanded="expanded ? 'true' : 'false'"
      data-testid="setup-template-dependencies__header"
      @click="toggleExpanded"
    >
      <span class="template-dependency-section__header-left">
        <span
          class="template-dependency-section__chevron"
          :class="{ 'template-dependency-section__chevron--expanded': expanded }"
        >
          <img
            src="/img/icons/fbc-icons/AccordionChevron.svg"
            alt=""
            class="template-dependency-section__chevron-icon"
          />
        </span>
        <span class="template-dependency-section__title">Dependencies</span>
      </span>
      <span class="template-dependency-section__header-right">
        <span
          v-if="!subjectDueReady"
          class="template-dependency-section__due-hint"
          data-testid="setup-template-dependencies__due-hint"
        >
          {{ copy.PICKER_DUE_REQUIRED }}
        </span>
        <span class="template-dependency-section__summary">
          {{ summaryLabel || copy.EMPTY_DIALOG_SUMMARY }}
        </span>
      </span>
    </button>

    <div v-show="expanded" class="template-dependency-section__body">
      <div
        v-if="chips.length"
        class="template-dependency-section__chips"
        data-testid="setup-template-dependencies__chips"
      >
        <span
          v-for="(chip, chipIndex) in chips"
          :key="`${templateIdString(chip.taskId)}-${chipIndex}`"
          class="template-dependency-section__chip"
          :data-testid="`setup-template-dependencies__chip--${chipIndex}`"
        >
          <v-tooltip
            bottom
            content-class="tooltip-bottom tooltip-wrap"
            open-delay="300"
            :disabled="!isChipTruncated(chipIndex)"
          >
            <template #activator="{ on, attrs }">
              <span
                ref="chipLabels"
                class="template-dependency-section__chip-label"
                v-bind="attrs"
                v-on="on"
              >
                {{ chipDisplayLabel(chip) }}
              </span>
            </template>
            <span>{{ chipLabel(chip) }}</span>
          </v-tooltip>
          <button
            type="button"
            class="template-dependency-section__chip-remove"
            :aria-label="`Remove ${chipLabel(chip)}`"
            :data-testid="`setup-template-dependencies__chip-remove--${chipIndex}`"
            @click="onChipRemove(chip)"
          >
            <v-icon
              size="20"
              class="template-dependency-section__chip-remove-icon"
              aria-hidden="true"
            >
              mdi-close
            </v-icon>
          </button>
        </span>
      </div>

      <div class="template-dependency-section__search">
        <v-text-field
          v-model="searchLocal"
          class="template-dependency-section__search-field"
          dense
          outlined
          hide-details
          :placeholder="copy.PICKER_SEARCH_PLACEHOLDER"
          data-testid="setup-template-dependencies__search"
          @input="onSearchInput"
        />
        <button
          v-if="searchLocal"
          type="button"
          class="template-dependency-section__search-clear"
          aria-label="Clear search"
          data-testid="setup-template-dependencies__search-clear"
          @click="clearSearch"
        >
          <v-icon size="16">mdi-close</v-icon>
        </button>
        <img
          v-else
          src="/img/icons/fbc-icons/SearchIcon.svg"
          alt=""
          class="template-dependency-section__search-icon"
        />
      </div>

      <div
        class="template-dependency-section__table-wrap fbc-table-header-labels"
        data-testid="setup-template-dependencies__table-wrap"
      >
        <v-progress-linear v-if="loading" indeterminate />
        <lb-datatableaction
          ref="dataTableAction"
          :headers="tableHeaders"
          :tabledata="records"
          :displayed-row-count="records.length"
          :itemsperpage="records.length || 20"
          :bulk-actions="false"
          :show-toolbar="false"
          :enable-column-reorder="false"
          :enable-cell-truncate="false"
          :page_details="{}"
          :selectfilter="{}"
          header-bg-class="fbc-table-header-bg"
          data-testid="setup-template-dependencies__datatable"
        >
          <template #tbody="{ headers }">
            <tr
              v-for="(row, rowIndex) in records"
              :key="templateIdString(row._id) || rowIndex"
              class="template-dependency-section__row"
              :class="{
                'template-dependency-section__row--selected': isRowLinked(row),
                'template-dependency-section__row--disabled': isRowVisuallyDisabled(row),
                'template-dependency-section__row--linked-editable': isRowLinked(row) && isRowVisuallyDisabled(row),
              }"
              :aria-disabled="isRowCheckboxDisabled(row) && !isRowLinked(row) ? 'true' : 'false'"
              :data-testid="`setup-template-dependencies__row--${rowIndex}`"
            >
              <td
                v-for="header in visibleHeaders(headers)"
                :key="`${header.value}-${rowIndex}`"
                :class="{
                  'px-4': header.value !== 'selected',
                  'template-dependency-section__cell-check checkbox-cell': header.value === 'selected',
                  'template-dependency-section__cell-details': header.value === 'taskDetails',
                  'template-dependency-section__cell-due': header.value === 'due',
                  'template-dependency-section__cell-location': header.value === 'location',
                  'template-dependency-section__cell-link': header.value === 'linkType',
                }"
                :data-testid="`setup-template-dependencies__cell--${header.value}-${rowIndex}`"
                @click="header.value === 'selected' ? onCheckCellClick(row, $event) : undefined"
              >
                <v-checkbox
                  v-if="header.value === 'selected'"
                  dense
                  color="primary"
                  class="ma-0 template-dependency-section__checkbox"
                  :ripple="false"
                  hide-details
                  :input-value="isRowLinked(row)"
                  :disabled="isRowCheckboxDisabled(row)"
                  :tabindex="isRowCheckboxDisabled(row) ? -1 : 0"
                  :data-testid="`setup-template-dependencies__check--${rowIndex}`"
                  @click.native.stop
                  @change="onRowToggle(row, $event)"
                />
                <div
                  v-else-if="header.value === 'taskDetails'"
                  class="template-dependency-section__task-details"
                >
                  <span
                    v-if="row.ref"
                    class="template-dependency-section__ref"
                  >{{ row.ref }}</span>
                  <v-tooltip
                    bottom
                    fixed
                    open-delay="300"
                    content-class="tooltip-bottom tooltip-wrap"
                    class="template-dependency-section__name-tooltip"
                    :disabled="descriptionLabel(row.description) === String(row.description || '').trim()"
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        class="template-dependency-section__name"
                        v-bind="attrs"
                        v-on="on"
                      >{{ descriptionLabel(row.description) }}</span>
                    </template>
                    <span>{{ row.description }}</span>
                  </v-tooltip>
                  <v-tooltip
                    v-if="rowDisabledTooltip(row)"
                    bottom
                    fixed
                    open-delay="200"
                    content-class="tooltip-bottom template-dependency-section__disabled-tooltip"
                  >
                    <template #activator="{ on, attrs }">
                      <button
                        type="button"
                        class="template-dependency-section__info-btn"
                        :class="{ 'template-dependency-section__info-btn--warn': rowDisabledTooltip(row).warn }"
                        v-bind="attrs"
                        :data-testid="`setup-template-dependencies__info--${rowIndex}`"
                        v-on="on"
                      >
                        <img
                          v-if="rowDisabledTooltip(row).warn"
                          src="/img/icons/fbc-icons/AutoApprovalRejectedAlert.svg"
                          alt=""
                          class="template-dependency-section__warn-icon"
                        />
                        <v-icon v-else size="16">mdi-information-outline</v-icon>
                      </button>
                    </template>
                    <div class="template-dependency-section__disabled-tooltip-content">
                      <p class="template-dependency-section__disabled-tooltip-title">
                        {{ rowDisabledTooltip(row).title }}
                      </p>
                      <p class="template-dependency-section__disabled-tooltip-body">
                        {{ rowDisabledTooltip(row).body }}
                      </p>
                    </div>
                  </v-tooltip>
                </div>
                <template v-else-if="header.value === 'due'">
                  {{ formatDue(row.duedays) }}
                </template>
                <template v-else-if="header.value === 'location'">
                  <v-tooltip
                    bottom
                    fixed
                    open-delay="300"
                    content-class="tooltip-bottom tooltip-wrap"
                    class="template-dependency-section__location-tooltip"
                    :disabled="!row.locationName"
                  >
                    <template #activator="{ on, attrs }">
                      <span
                        class="template-dependency-section__location"
                        v-bind="attrs"
                        v-on="on"
                      >{{ row.locationName || '—' }}</span>
                    </template>
                    <span>{{ row.locationName }}</span>
                  </v-tooltip>
                </template>
                <div
                  v-else-if="header.value === 'linkType' && showsLinkType(row)"
                  class="template-dependency-section__link-type-wrap"
                >
                  <lb-dropdown
                    class="template-dependency-section__link-type"
                    :value="linkTypeForRow(row)"
                    :items="linkTypeItemsForRow(row)"
                    itemtext="label"
                    itemvalue="value"
                    :searchable="false"
                    :hidedetails="true"
                    :attach="false"
                    :flip-menu="true"
                    menu-content-class="dependency-link-type-menu"
                    appendiconinner="mdi-chevron-down"
                    label=""
                    placeholder=""
                    :data-testid="`setup-template-dependencies__link-type--${rowIndex}`"
                    @change="onLinkTypeChange(row, $event)"
                  />
                </div>
                <span
                  v-else-if="header.value === 'linkType'"
                  class="template-dependency-section__link-empty"
                >-</span>
              </td>
            </tr>
            <tr v-if="!loading && !records.length">
              <td
                :colspan="visibleHeaders(headers).length"
                class="template-dependency-section__table-empty"
              >
                {{ copy.PICKER_EMPTY }}
              </td>
            </tr>
          </template>
        </lb-datatableaction>
      </div>

      <div
        v-if="!chips.length"
        class="template-dependency-section__empty"
        data-testid="setup-template-dependencies__empty"
      >
        <p class="template-dependency-section__empty-title">
          {{ copy.EMPTY_LINKED_TITLE }}
        </p>
        <p class="template-dependency-section__empty-body">
          {{ copy.EMPTY_BLOCKING_BODY }}
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import './TemplateDependencySection.scss';
import { mapActions } from 'vuex';
import { SETUP_TEMPLATE_DEPENDENCY } from '../../../../../utils/constants';
import {
  dependencyChipLabel,
  truncateDependencyDescription,
  dependencySummaryLabel,
  blocksLinkPickerHint,
  collectBlocksLinkReasons,
  defaultPickerLinkType,
  formatTemplateDueDaysLabel,
  hasSubjectDuedays,
  isPickerRowInteractionDisabled,
  isPickerRowLinked,
  isSetupTemplatePickerRowDisabled,
  linkPickerDisabledTooltip,
  linkTypeItemsForPickerRow,
  mergeLinkedPickerRecords,
  rememberPickerChipDetails,
  pickerRowId,
  templateIdString,
} from '../../utils/setupDependencyUtils';

export default {
  name: 'TemplateDependencySection',

  props: {
    subjectDuedays: { type: [Number, String], default: '' },
    excludeTaskId: { type: String, default: '' },
    value: { type: Array, default: () => [] },
    expanded: { type: Boolean, default: false },
  },

  data() {
    return {
      copy: SETUP_TEMPLATE_DEPENDENCY,
      linkTypeOptions: SETUP_TEMPLATE_DEPENDENCY.PICKER_LINK_TYPE_OPTIONS,
      searchLocal: '',
      searchDebounce: null,
      suppressSearchReload: false,
      loadGeneration: 0,
      loading: false,
      records: [],
      page: 1,
      totalPages: 1,
      chips: [],
      scrollParent: null,
    };
  },

  computed: {
    summaryLabel() {
      return dependencySummaryLabel(this.chips);
    },
    subjectDueReady() {
      return hasSubjectDuedays(this.subjectDuedays);
    },
    locationHeader() {
      return this.$store.state.LocationHeader || this.copy.COL_LOCATION;
    },
    linkedTaskIds() {
      return new Set(
        (this.chips || [])
          .map((chip) => templateIdString(chip.taskId))
          .filter(Boolean)
      );
    },
    tableHeaders() {
      return [
        {
          text: '',
          value: 'selected',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'template-dependency-section__col-check',
        },
        {
          text: this.copy.COL_TASK_DETAILS,
          value: 'taskDetails',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'template-dependency-section__col-details',
        },
        {
          text: this.copy.COL_DUE,
          value: 'due',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'template-dependency-section__col-due',
        },
        {
          text: this.locationHeader,
          value: 'location',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'template-dependency-section__col-location',
        },
        {
          text: this.copy.COL_LINK_TYPE,
          value: 'linkType',
          visible: true,
          sortable: false,
          filterable: false,
          headerClass: 'template-dependency-section__col-link',
        },
      ];
    },
  },

  watch: {
    value: {
      immediate: true,
      deep: true,
      handler(val) {
        this.chips = Array.isArray(val) ? val.map((c) => ({ ...c })) : [];
      },
    },
    subjectDuedays() {
      if (!this.expanded) {
        this.keepSelectedRowDetails();
        this.records = [];
        return;
      }
      this.resetPickerAndLoad();
    },
    expanded(val) {
      if (!val) {
        this.clearSearchSilently();
        return;
      }
      this.resetPickerAndLoad();
    },
    chips: {
      deep: true,
      handler() {
        this.records = this.mergePickerRecords(this.records);
      },
    },
  },

  mounted() {
    if (this.expanded) this.resetPickerAndLoad();
    this.$nextTick(() => {
      this.bindScrollParent();
    });
  },

  beforeDestroy() {
    this.unbindScrollParent();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.records = [];
    this.chips = [];
  },

  methods: {
    ...mapActions('setup', ['fetchTemplatesForLink']),

    templateIdString,

    toggleExpanded() {
      this.$emit('update:expanded', !this.expanded);
    },

    chipLabel(chip) {
      return dependencyChipLabel(chip);
    },

    chipDisplayLabel(chip) {
      return dependencyChipLabel({
        ...chip,
        description: truncateDependencyDescription(chip?.description),
      });
    },

    descriptionLabel(value) {
      return truncateDependencyDescription(value);
    },

    isChipTruncated(index) {
      const chip = this.chips[index];
      if (!chip) return false;
      return this.chipDisplayLabel(chip) !== this.chipLabel(chip);
    },

    rowDisabledTooltip(row) {
      if (this.isRowLinked(row)) return null;
      const disabledRow = linkPickerDisabledTooltip(row);
      if (disabledRow) return disabledRow;
      const blocksHint = blocksLinkPickerHint(row, this.copy.PICKER_DISABLED_REASON_MESSAGES);
      if (!blocksHint || this.isRowLinked(row)) return null;
      const code = collectBlocksLinkReasons(row)[0]?.code;
      return {
        title: 'Blocks',
        body: blocksHint,
        warn: code === 'DUE_ORDER' || code === 'MISSING_DUE',
      };
    },

    visibleHeaders(headers) {
      return (headers || this.tableHeaders).filter((header) => header.visible !== false);
    },

    formatDue(duedays) {
      return formatTemplateDueDaysLabel(duedays);
    },

    emitChips() {
      this.$emit('input', this.chips.map((c) => ({ ...c })));
    },

    mergePickerRecords(records, options = {}) {
      return mergeLinkedPickerRecords(records, this.chips, {
        includeMissingChips: !String(this.searchLocal || '').trim(),
        // Checking a row keeps it in place. A due-date or search reload
        // pins already selected tasks above the new API order.
        sortSelectedFirst: options.sortSelectedFirst === true,
      });
    },

    resetPickerAndLoad() {
      this.keepSelectedRowDetails();
      this.page = 1;
      this.records = [];
      this.loadPage(1, true);
    },

    keepSelectedRowDetails() {
      const nextChips = rememberPickerChipDetails(this.chips, this.records);
      const changed = nextChips.some((chip, index) => chip !== this.chips[index]);
      if (!changed) return;
      this.chips = nextChips;
      this.emitChips();
    },

    clearSearchSilently() {
      this.keepSelectedRowDetails();
      if (this.searchDebounce) {
        clearTimeout(this.searchDebounce);
        this.searchDebounce = null;
      }
      this.suppressSearchReload = true;
      this.loadGeneration += 1;
      this.loading = false;
      this.searchLocal = '';
      this.records = [];
      this.page = 1;
      this.totalPages = 1;
      this.$nextTick(() => {
        this.suppressSearchReload = false;
      });
    },

    async loadPage(pageNum, replace = false) {
      const generation = ++this.loadGeneration;
      this.loading = true;
      try {
        const result = await this.fetchTemplatesForLink({
          duedays: this.subjectDueReady ? Number(this.subjectDuedays) : undefined,
          excludeTaskId: this.excludeTaskId || undefined,
          page: pageNum,
          search: this.searchLocal || '',
        });
        if (generation !== this.loadGeneration) return;
        const next = result.records || [];
        const batch = replace ? next : this.records.concat(next);
        this.records = this.mergePickerRecords(batch, { sortSelectedFirst: true });
        this.page = result.page || pageNum;
        this.totalPages = result.totalPages || 1;
      } catch (err) {
        if (generation !== this.loadGeneration) return;
        console.error('[TemplateDependencySection]:', err?.message || err);
        this.$store.commit(
          'sbError',
          err?.message || 'Failed to load tasks',
          { root: true }
        );
      } finally {
        if (generation === this.loadGeneration) this.loading = false;
      }
    },

    onSearchInput() {
      if (this.suppressSearchReload) return;
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.resetPickerAndLoad();
      }, 350);
    },

    clearSearch() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.searchLocal = '';
      this.resetPickerAndLoad();
    },

    bindScrollParent() {
      this.unbindScrollParent();
      const el = this.$el?.closest('.add-template-dialog__body');
      if (!el) return;
      this.scrollParent = el;
      el.addEventListener('scroll', this.onTableScroll, { passive: true });
    },

    unbindScrollParent() {
      if (!this.scrollParent) return;
      this.scrollParent.removeEventListener('scroll', this.onTableScroll);
      this.scrollParent = null;
    },

    onTableScroll(e) {
      const el = e?.currentTarget || e?.target || this.scrollParent;
      if (!el || this.loading || this.page >= this.totalPages) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 48;
      if (nearBottom) {
        this.loadPage(this.page + 1, false);
      }
    },

    chipIndexForRow(row) {
      const id = pickerRowId(row);
      return this.chips.findIndex((c) => templateIdString(c.taskId) === id);
    },

    isRowLinked(row) {
      return isPickerRowLinked(row, this.linkedTaskIds);
    },

    isWaitingOnRow(row) {
      return this.linkTypeForRow(row) === this.copy.LINK_TYPE_BLOCKING;
    },

    showsLinkType(row) {
      return this.isRowLinked(row) && !this.isWaitingOnRow(row);
    },

    isRowSelected(row) {
      return this.isRowLinked(row);
    },

    isRowPickerDisabled(row) {
      return isSetupTemplatePickerRowDisabled(row);
    },

    isRowVisuallyDisabled(row) {
      if (this.isRowLinked(row)) return false;
      return this.isRowPickerDisabled(row);
    },

    isRowCheckboxDisabled(row) {
      if (!this.subjectDueReady) return true;
      return isPickerRowInteractionDisabled(row, this.isRowLinked(row));
    },

    linkTypeForRow(row) {
      const idx = this.chipIndexForRow(row);
      return idx >= 0 ? this.chips[idx].linkType : '';
    },

    linkTypeItemsForRow(row) {
      const current = this.linkTypeForRow(row);
      return linkTypeItemsForPickerRow(row, this.linkTypeOptions).map((item) => (
        item.value === current ? { ...item, disabled: false } : item
      ));
    },

    onCheckCellClick(row, event) {
      if (this.isRowCheckboxDisabled(row)) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (event.target.closest('.v-input--selection-controls')) return;
      this.onRowToggle(row, !this.isRowLinked(row));
    },

    onRowToggle(row, checked) {
      const id = pickerRowId(row);
      if (!id) return;
      if (this.isRowCheckboxDisabled(row)) return;
      const idx = this.chipIndexForRow(row);
      if (checked) {
        if (idx >= 0) return;
        this.chips.push({
          taskId: id,
          ref: row.ref,
          description: row.description,
          duedays: row.duedays,
          locationName: row.locationName || '',
          linkType: defaultPickerLinkType(row),
        });
      } else if (idx >= 0) {
        this.chips.splice(idx, 1);
      }
      this.emitChips();
    },

    onLinkTypeChange(row, linkType) {
      const idx = this.chipIndexForRow(row);
      if (idx < 0) return;
      this.$set(this.chips[idx], 'linkType', linkType);
      if (row.duedays != null && row.duedays !== '') {
        this.$set(this.chips[idx], 'duedays', row.duedays);
      }
      this.emitChips();
    },

    onChipRemove(chip) {
      const id = templateIdString(chip?.taskId);
      if (!id) return;
      const idx = this.chips.findIndex((c) => templateIdString(c.taskId) === id);
      if (idx < 0) return;
      this.chips.splice(idx, 1);
      this.emitChips();
    },
  },
};
</script>
